import { toast } from 'react-hot-toast';
import { useWalletStore } from '../store/walletStore';

export const EXPLORER_TX_URL = 'https://vexscan.io/tx/';

/** Turn a wallet / chain failure into something a user can act on. */
function describeWalletError(err) {
  const msg = (err && err.message) || String(err || 'Unknown wallet error');
  if (/rejected/i.test(msg)) return 'Transaction rejected in VexSpark.';
  if (/timed out/i.test(msg)) return 'Timed out waiting for approval in VexSpark.';
  if (/popup was closed/i.test(msg)) return 'The VexSpark window was closed before the transaction was approved.';
  if (/did not respond/i.test(msg)) return 'Could not open VexSpark — allow pop-ups for this site and try again.';
  return msg;
}

/**
 * ensureConnected(): true when a wallet is connected. Otherwise it runs the normal
 * connect flow (errors are toasted, never thrown) and returns false — the user
 * then presses the action again.
 * (Chaining connect → sign in one click races the popup's own auto-close after
 * connecting, and trips pop-up blockers.)
 *
 * signAndSend(): sign `ops` (OperationJson[]) in the user's VexSpark wallet and submit them.
 *
 * - Not connected → ensureConnected() flow, resolves to null (nothing signed).
 * - Resolves to the tx hash the wallet reports once the node ADMITTED the bundle.
 *   Execution happens in a later block — check the explorer for the outcome.
 * - Throws an Error with a user-facing message on rejection / timeout / refusal.
 *
 * maxGas is deliberately NOT passed unless the caller sets one: VexSpark then
 * prices the bundle per operation (bundle-builder.ts computeMaxGas).
 */
export async function ensureConnected() {
  const wallet = useWalletStore.getState();
  if (wallet.isConnected) return true;
  toast('Connect your VexSpark wallet to sign this transaction.', { icon: '🔑' });
  try {
    await wallet.connect();
  } catch (err) {
    toast.error(`Wallet connection failed: ${describeWalletError(err)}`);
    return false;
  }
  toast.success('Wallet connected — press the button again to sign.');
  return false;
}

export async function signAndSend(ops, description, { maxGas } = {}) {
  if (!(await ensureConnected())) return null;
  try {
    return await useWalletStore.getState().signAndSubmit(ops, {
      appName: 'VexForge',
      description,
      ...(maxGas ? { maxGas } : {}),
    });
  } catch (err) {
    throw new Error(describeWalletError(err));
  }
}
